// =============================================================================
// PTEL - AuditLogViewer (FB.11.03)
// Tabla de registro de auditoría con filtros por acción y rango de fechas
// Integrado como sección en AdminPanel
// =============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { ClipboardList, RefreshCw, X, ChevronDown, ChevronRight } from 'lucide-react';
import {
  watchAuditLog,
  formatAuditDescription,
  type QueryAuditParams,
  type AuditAction,
  type AuditEntry,
} from '@/services/firebase/audit.service';
import type { AuthTokenSet } from '@/components/auth/auth.tokens';
import { useIsMounted } from '@/hooks/useIsMounted';

// -----------------------------------------------------------------------------
// Types & Constants
// -----------------------------------------------------------------------------

interface AuditLogViewerProps {
  t: AuthTokenSet;
}

type FilterAction = AuditAction | 'all';

// -----------------------------------------------------------------------------
// PANADM-b2 DOP-6 (a): mismo aviso de fallo que `AdminPanel` (PANADM-b1):
// motivo, código y «Reintentar»; «vacío de verdad» solo si la lectura fue bien.
// -----------------------------------------------------------------------------

interface LoadError {
  message: string;
  code: string;
}

/** El código de Firebase va aparte; el texto es el propio de la lista, en español. */
function toLoadError(err: unknown, message: string): LoadError {
  const code = (err as { code?: unknown } | null)?.code;
  return { message, code: typeof code === 'string' && code ? code : 'unknown' };
}

function LoadErrorNotice({ testId, error, what, onRetry, t }: {
  testId: string; error: LoadError; what: string; onRetry: () => void; t: AuthTokenSet;
}) {
  return (
    <div data-testid={testId} role="alert" style={{
      padding: '12px', background: t.errorBg,
      border: `1px solid ${t.error}30`, borderRadius: '8px',
      color: t.error, fontSize: '13px', marginBottom: '12px',
    }}>
      <div>
        {error.message} Motivo: <span data-testid={`${testId}-code`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{error.code}</span>.
        {' '}No significa que no haya {what}: no se ha podido leer.
      </div>
      <button
        type="button"
        data-testid={`${testId}-retry`}
        onClick={onRetry}
        style={{
          marginTop: '8px', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
          background: 'transparent', border: `1px solid ${t.error}`, color: t.error,
          fontSize: '12px', fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        Reintentar
      </button>
    </div>
  );
}

const ACTION_CONFIG: Record<AuditAction, { label: string; color: string }> = {
  create:  { label: 'Crear',      color: '#22c55e' },
  update:  { label: 'Actualizar', color: '#3b82f6' },
  delete:  { label: 'Eliminar',   color: '#ef4444' },
  approve: { label: 'Aprobar',    color: '#a3e635' },
  reject:  { label: 'Rechazar',   color: '#f59e0b' },
  login:   { label: 'Login',      color: '#8b5cf6' },
  logout:  { label: 'Logout',     color: '#6b7280' },
  expire:  { label: 'Expirar',    color: '#f97316' },
};

const ACTION_TOOLTIPS: Record<AuditAction, string> = {
  create:  'Altas de infraestructuras, patrones o documentos',
  update:  'Modificaciones de campos en registros existentes',
  delete:  'Borrados definitivos de registros',
  approve: 'Propuestas aprobadas por un moderador o administrador',
  reject:  'Propuestas rechazadas por un moderador o administrador',
  login:   'Inicios de sesión en PTEL Tool',
  logout:  'Cierres de sesión manuales',
  expire:  'Caducidades automáticas de rol o sesión',
};

const ACTIONS: AuditAction[] = ['create', 'update', 'delete', 'approve', 'reject', 'login', 'logout', 'expire'];

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatTimestamp(date: Date): string {
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncate(text: string, max: number): string {
  return text.length > max ? text.slice(0, max) + '…' : text;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function AuditLogViewer({ t }: AuditLogViewerProps) {
  const [filterAction, setFilterAction] = useState<FilterAction>('all');
  const [filterINE, setFilterINE] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Último resultado de la escucha, marcado con la consulta a la que pertenece.
  const [result, setResult] = useState<{ key: string; entries: AuditEntry[]; error: LoadError | null } | null>(null);
  // «Recargar» y «Reintentar» vuelven a abrir la escucha (tras un fallo, Firestore la corta).
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(k => k + 1);

  // SSCA-FIX-1: guard de montaje para los setState que llegan de la escucha.
  const isMounted = useIsMounted();

  const params = useMemo<QueryAuditParams>(() => {
    const p: QueryAuditParams = { maxResults: 100 };
    if (filterAction !== 'all') p.action = filterAction;
    if (filterINE.trim().length >= 5) p.municipalityINE = filterINE.trim();
    if (startDate) p.startDate = new Date(startDate + 'T00:00:00');
    if (endDate) p.endDate = new Date(endDate + 'T23:59:59');
    return p;
  }, [filterAction, filterINE, startDate, endDate]);
  const queryKey = `${JSON.stringify(params)}#${reloadKey}`;

  // PANADM-b2 DOP-2 (b): el registro escucha en vivo; una acción nueva aparece sin
  // «Recargar». Cambiar un filtro o desmontar deja de escuchar la consulta anterior.
  useEffect(() => {
    let active = true;
    const unsubscribe = watchAuditLog(
      params,
      (list) => {
        if (!active || !isMounted()) return;
        setResult({ key: queryKey, entries: list, error: null });
      },
      (err) => {
        // PANADM-b2 DOP-5 (a): el fallo se dice como fallo; no se deja la lista vieja.
        if (!active || !isMounted()) return;
        setResult({
          key: queryKey,
          entries: [],
          error: toLoadError(err, 'No se ha podido leer el registro de auditoría.'),
        });
      },
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [params, queryKey, isMounted]);

  // «Cargando» mientras el último resultado no sea de la consulta en curso.
  const loading = result?.key !== queryKey;
  const entries = !loading && result ? result.entries : [];
  const error = !loading && result ? result.error : null;

  const hasFilters = filterAction !== 'all' || startDate || endDate || filterINE.trim().length >= 5;
  const clearFilters = () => {
    setFilterAction('all');
    setStartDate('');
    setEndDate('');
    setFilterINE('');
  };

  // --- Action pills ---
  const pills: { key: FilterAction; label: string; color: string; tooltip: string }[] = [
    { key: 'all', label: 'Todas', color: t.primary, tooltip: 'Mostrar todas las acciones sin filtrar por tipo' },
    ...ACTIONS.map(a => ({ key: a as FilterAction, ...ACTION_CONFIG[a], tooltip: ACTION_TOOLTIPS[a] })),
  ];

  return (
    <div data-testid="audit-log-viewer">
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ClipboardList size={16} style={{ color: t.primary }} />
          <span style={{
            fontSize: '14px', fontWeight: 600, color: t.text,
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            Registro de auditoría
          </span>
        </div>
        <button
          data-testid="audit-refresh"
          onClick={reload}
          title="Vuelve a lanzar la consulta actual contra Firestore (no cambia los filtros)"
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            background: 'none', border: `1px solid ${t.border}`, borderRadius: '6px',
            padding: '4px 8px', cursor: 'pointer', color: t.textMuted, fontSize: '11px',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          <RefreshCw size={12} />
          Recargar
        </button>
      </div>

      {/* Filter pills — action type */}
      <div style={{ display: 'flex', gap: '5px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {pills.map(p => {
          const isActive = filterAction === p.key;
          return (
            <button
              key={p.key}
              data-testid={`audit-filter-${p.key}`}
              onClick={() => setFilterAction(p.key)}
              title={p.tooltip}
              style={{
                padding: '3px 9px', borderRadius: '12px', fontSize: '11px',
                fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer',
                border: `1px solid ${isActive ? p.color : t.border}`,
                background: isActive ? `${p.color}15` : 'transparent',
                color: isActive ? p.color : t.textMuted, fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s',
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Date range filters + INE filter */}
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap',
      }}>
        <label style={{ fontSize: '11px', color: t.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
          INE:
          <input
            data-testid="audit-filter-ine"
            type="text"
            value={filterINE}
            onChange={(e) => setFilterINE(e.target.value)}
            placeholder="18052"
            maxLength={5}
            title="Código INE de 5 dígitos del municipio (p. ej., 18051 Colomera). El filtro se aplica al completar los 5 caracteres"
            style={{
              marginLeft: '4px', padding: '3px 6px', background: t.bgInput,
              border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text,
              fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", width: '60px',
            }}
          />
        </label>
        <label style={{ fontSize: '11px', color: t.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
          Desde:
          <input
            data-testid="audit-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            title="Fecha inicial del rango. Las acciones anteriores quedan fuera"
            style={{
              marginLeft: '4px', padding: '3px 6px', background: t.bgInput,
              border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text,
              fontSize: '11px', fontFamily: "'JetBrains Mono', monospace",
            }}
          />
        </label>
        <label style={{ fontSize: '11px', color: t.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>
          Hasta:
          <input
            data-testid="audit-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            title="Fecha final del rango. Las acciones posteriores quedan fuera"
            style={{
              marginLeft: '4px', padding: '3px 6px', background: t.bgInput,
              border: `1px solid ${t.border}`, borderRadius: '4px', color: t.text,
              fontSize: '11px', fontFamily: "'JetBrains Mono', monospace",
            }}
          />
        </label>
        {hasFilters && (
          <button
            data-testid="audit-clear-filters"
            onClick={clearFilters}
            title="Restablece todos los filtros: acción, INE y rango de fechas"
            style={{
              display: 'flex', alignItems: 'center', gap: '3px',
              background: 'none', border: 'none', cursor: 'pointer',
              color: t.primary, fontSize: '11px', padding: 0,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            <X size={11} />
            Limpiar
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div data-testid="audit-loading" style={{
          textAlign: 'center', padding: '24px', color: t.textMuted, fontSize: '12px',
        }}>
          Cargando auditoría…
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <LoadErrorNotice
          testId="audit-error"
          error={error}
          what="entradas de auditoría"
          onRetry={reload}
          t={t}
        />
      )}

      {/* Table */}
      {!loading && entries.length > 0 && (
        <table data-testid="audit-table" style={{
          width: '100%', borderCollapse: 'collapse', fontSize: '11px',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          <thead>
            <tr>
              {['', 'Fecha', 'Usuario', 'Acción', 'Descripción', 'ID entidad'].map(h => (
                <th key={h} style={{
                  textAlign: 'left', padding: '6px 4px',
                  borderBottom: `1px solid ${t.border}`, color: t.textMuted,
                  fontSize: '10px', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => {
              const cfg = ACTION_CONFIG[entry.action];
              const isExpanded = expandedId === entry.id;
              const hasDiff = entry.before !== null || entry.after !== null;
              return (
                <React.Fragment key={entry.id}>
                  <tr
                    data-testid={`audit-row-${entry.id}`}
                    onClick={() => hasDiff && setExpandedId(isExpanded ? null : entry.id)}
                    style={{ cursor: hasDiff ? 'pointer' : 'default' }}
                  >
                    <td style={{ padding: '5px 4px', borderBottom: `1px solid ${t.border}`, width: '16px' }}>
                      {hasDiff && (
                        isExpanded
                          ? <ChevronDown size={12} style={{ color: t.textMuted }} />
                          : <ChevronRight size={12} style={{ color: t.textMuted }} />
                      )}
                    </td>
                    <td style={{ padding: '5px 4px', borderBottom: `1px solid ${t.border}`, color: t.textMuted, whiteSpace: 'nowrap' }}>
                      {formatTimestamp(entry.timestamp)}
                    </td>
                    <td style={{ padding: '5px 4px', borderBottom: `1px solid ${t.border}`, color: t.text }}>
                      {truncate(entry.userEmail, 25)}
                    </td>
                    <td style={{ padding: '5px 4px', borderBottom: `1px solid ${t.border}` }}>
                      <span style={{
                        display: 'inline-block', padding: '1px 6px', borderRadius: '8px',
                        background: `${cfg.color}18`, color: cfg.color,
                        fontSize: '10px', fontWeight: 600,
                      }}>
                        {cfg.label}
                      </span>
                    </td>
                    <td style={{ padding: '5px 4px', borderBottom: `1px solid ${t.border}`, color: t.textMuted }}>
                      {formatAuditDescription(entry)}
                    </td>
                    <td style={{ padding: '5px 4px', borderBottom: `1px solid ${t.border}`, color: t.textMuted, fontFamily: "'JetBrains Mono', monospace", fontSize: '10px' }}>
                      {truncate(entry.entityId, 20)}
                    </td>
                  </tr>
                  {isExpanded && hasDiff && (
                    <tr key={`${entry.id}-detail`} data-testid={`audit-detail-${entry.id}`}>
                      <td colSpan={6} style={{
                        padding: '8px 12px', borderBottom: `1px solid ${t.border}`,
                        background: `${t.bgInput}`,
                      }}>
                        <div style={{ display: 'flex', gap: '16px', fontSize: '11px', fontFamily: "'JetBrains Mono', monospace" }}>
                          {entry.before && (
                            <div style={{ flex: 1 }}>
                              <div style={{ color: t.textMuted, marginBottom: '4px', fontWeight: 600 }}>Antes</div>
                              <pre data-testid={`audit-before-${entry.id}`} style={{
                                margin: 0, color: '#f87171', background: 'rgba(248,113,113,0.06)',
                                padding: '6px', borderRadius: '4px', fontSize: '10px',
                                overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                              }}>
                                {JSON.stringify(entry.before, null, 2)}
                              </pre>
                            </div>
                          )}
                          {entry.after && (
                            <div style={{ flex: 1 }}>
                              <div style={{ color: t.textMuted, marginBottom: '4px', fontWeight: 600 }}>Después</div>
                              <pre data-testid={`audit-after-${entry.id}`} style={{
                                margin: 0, color: '#4ade80', background: 'rgba(74,222,128,0.06)',
                                padding: '6px', borderRadius: '4px', fontSize: '10px',
                                overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                              }}>
                                {JSON.stringify(entry.after, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Empty state */}
      {!loading && !error && entries.length === 0 && (
        <div data-testid="audit-empty" style={{
          textAlign: 'center', padding: '24px', color: t.textMuted, fontSize: '12px',
        }}>
          <ClipboardList size={20} style={{ opacity: 0.3, marginBottom: '6px' }} />
          <div>Sin entradas de auditoría</div>
        </div>
      )}

      {/* Footer */}
      {!loading && entries.length > 0 && (
        <div style={{
          marginTop: '8px', fontSize: '10px', color: t.textMuted, textAlign: 'center',
        }}>
          {entries.length} entrada{entries.length !== 1 ? 's' : ''}
          {hasFilters ? ' (filtrado)' : ''}
        </div>
      )}
    </div>
  );
}
